/**
 * The session drawer's card for a session a decided approval stopped (R-HL-3): what was asked,
 * when, what happened, and Approve and resume (R-HL-4) or Mark as handled; a redirect (a
 * person's denial with a note) leads with Mark as handled and keeps Approve and resume in a menu
 * behind its confirm. Renders nothing for a
 * session that is not blocked (a pending request has its own card, SessionApprovals).
 */
import { useState } from 'react';
import { Link } from 'react-router';
import { Check, Loader, ShieldAlert, ShieldCheck, ShieldX } from '@orgus/ui/icons';
import type { ApprovalBlock, Session } from '@orgus/protocol';
import { Button, RelativeTime, cn, toUiError, toast } from '@orgus/ui';
import type { ButtonProps, UiError } from '@orgus/ui';
import { useMachine } from '../../lib/store.ts';
import { useCan } from '../auth/useMe.ts';
import { usePersonName } from '../missions/people.ts';
import { useApprovals } from './live.ts';
import { ApproveAndResumeButton, ResumeError, ResumeMenu } from './ApproveAndResumeButton.tsx';
import { blockView, dismissApproval, grantUntil, useApprovalBlock, useMeId } from './blocked.ts';
import type { BlockView } from './blocked.ts';

/**
 * A neutral card whatever the state (live brief 4.7: no full red fill, no pink blocks in light):
 * the state is the icon and a soft pill in the header only.
 */
const CARD = 'border-border bg-surface shadow-elevation-flat';
const TONE = {
  failed: { box: CARD, fg: 'text-failed', pill: '' },
  working: { box: CARD, fg: 'text-working', pill: '' },
  waiting: { box: CARD, fg: 'text-waiting', pill: '' },
} as const;

function BlockIcon({ v }: { v: BlockView }) {
  const cls = cn('size-4 shrink-0', TONE[v.tone].fg);
  if (v.kind === 'resuming') return <Loader className={cn(cls, 'motion-safe:animate-spin')} aria-hidden />;
  if (v.kind === 'live') return <ShieldCheck className={cls} aria-hidden />;
  if (v.kind === 'policy') return <ShieldX className={cls} aria-hidden />;
  return <ShieldAlert className={cls} aria-hidden />;
}

/**
 * Mark as handled: the person says this no longer blocks anything. Ghost beside Approve and
 * resume; primary when it leads (a redirect).
 */
export function MarkHandledButton({ approvalId, variant = 'ghost', size = 'sm' }: { approvalId: string; variant?: ButtonProps['variant']; size?: ButtonProps['size'] }) {
  const can = useCan('operator');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await dismissApproval(approvalId);
      toast('Marked as handled', { tone: 'neutral', description: 'It no longer blocks the session or its mission.' });
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="inline-flex min-w-0 flex-col items-start gap-2">
      <Button size={size} variant={variant} icon={<Check />} loading={busy} disabled={!can} title={can ? undefined : 'Viewers cannot change approvals. Ask an operator.'} onClick={() => void run()}>
        Mark as handled
      </Button>
      {error && <ResumeError error={error} />}
    </span>
  );
}

/** "Blocked: approval expired" reads "Approval expired" on the card: the header's badge says Blocked. */
export function blockTitle(label: string): string {
  const rest = label.replace(/^Blocked:\s*/, '');
  return rest ? rest.charAt(0).toUpperCase() + rest.slice(1) : label;
}

/** The card body for one block: pure enough to render in tests (no store reads beyond its children). */
export function ApprovalBlockCard({
  block,
  v,
  approvalId,
  decidedAt,
  why = true,
}: {
  block: ApprovalBlock;
  v: BlockView;
  approvalId: string;
  decidedAt: number;
  /** The dim why line under Mark as handled; off where the page's headline already says it. */
  why?: boolean;
}) {
  return (
    <div role="group" aria-label={v.label} className={cn('flex flex-col gap-2 rounded-card border p-3', TONE[v.tone].box)}>
      <div className="flex items-center gap-2">
        <span className={cn('inline-flex h-6 min-w-0 items-center gap-1', TONE[v.tone].pill)}>
          <BlockIcon v={v} />
          {/* The status is the icon; the header's badge already says Blocked, so the title is the cause, in plain text. */}
          <span className="truncate text-ui font-medium text-fg">{blockTitle(v.label)}</span>
        </span>
        <span className="flex-1" />
        <RelativeTime ts={decidedAt} className="shrink-0 text-sm text-fg-subtle" />
      </div>
      <p className="text-sm text-fg-muted">{v.tool ? `It asked to use ${v.tool}:` : 'It asked:'}</p>
      {v.command && <pre className="max-h-32 overflow-auto rounded-control bg-surface p-2 font-mono text-sm whitespace-pre-wrap break-all text-fg">{v.command}</pre>}
      <p className="text-sm text-fg-muted">{v.when}</p>
      {v.note && <p className="text-sm text-fg">{v.note}</p>}
      {v.detail && <p className="text-sm text-fg">{v.detail}</p>}
      {v.kind !== 'resuming' && v.lead === 'handled' && (
        <div className="flex flex-col gap-1 pt-1">
          <div className="flex flex-wrap items-start gap-2">
            <MarkHandledButton approvalId={approvalId} variant="primary" />
            <ResumeMenu approvalId={approvalId} redirect={v.redirect} />
          </div>
          {why && v.why && <p className="text-caption text-fg-subtle">{v.why}</p>}
        </div>
      )}
      {v.kind !== 'resuming' && v.lead !== 'handled' && (
        <div className="flex flex-wrap items-start gap-2 pt-1">
          {v.resumable && <ApproveAndResumeButton approvalId={approvalId} />}
          {v.kind === 'policy' && (
            <Link to="/settings/policies" className="inline-flex h-7 items-center rounded-control px-2 text-sm font-medium text-fg-muted underline-offset-2 hover:bg-hover hover:text-fg">
              Open the rule{v.ruleId ? ` ${v.ruleId}` : ''}
            </Link>
          )}
          <MarkHandledButton approvalId={approvalId} />
        </div>
      )}
      {!block.resumable && v.kind === 'policy' && <p className="text-sm text-fg-subtle">Policy refused this; to run it, change or turn off the rule and start the session again.</p>}
    </div>
  );
}

/** Mount: SessionDrawer, right after the pending approvals; the mission run page, without the why line its headline says. */
export function ApprovalBlocker({ session, why = true }: { session: Session; why?: boolean }) {
  const block = useApprovalBlock(session);
  const { approvals } = useApprovals();
  const machine = useMachine(session.machineId);
  const me = useMeId();
  const name = usePersonName();
  if (!block) return null;
  const v = blockView(block, { machineName: machine?.name ?? 'its machine', grantUntil: grantUntil(approvals, block.approval), sessionStatus: session.status, sessionOrigin: session.origin, me, name });
  return (
    <div className="mx-4 mt-4">
      <ApprovalBlockCard block={block} v={v} approvalId={block.approval.id} decidedAt={block.approval.decidedAt ?? block.approval.createdAt} why={why} />
    </div>
  );
}
